import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Sparkles, CheckCircle2, CreditCard, Receipt, QrCode, ExternalLink,
  AlertCircle, ChevronRight, Search, Minus, Plus, Tv,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { formatCents, cn } from '@/utils/cn';
import type { Plan, BillingCycle } from '@/types/plan';
import { CreditCardForm } from './CreditCardForm';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan;
  tvCount: number;
  billingCycle: BillingCycle;
  /**
   * Quando true, mostra seletor inline de plano, TVs e ciclo (usado pra reativação
   * após cancelamento). Default false (fluxo normal de ativação de trial).
   */
  allowEdit?: boolean;
}

type PaymentMethod = 'pix' | 'boleto' | 'credit_card';

interface ActivateResponse {
  subscription_id: number;
  asaas_id?: string;
  total_cents: number;
  invoice_url?: string;
  pix_qr_code?: string;
  pix_copy_paste?: string;
  boleto_url?: string;
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string; sub: string; icon: typeof QrCode }[] = [
  { value: 'pix', label: 'PIX', sub: 'Pagamento instantâneo · QR Code ou copia-e-cola', icon: QrCode },
  { value: 'boleto', label: 'Boleto', sub: 'Compensação em 1-2 dias úteis', icon: Receipt },
  { value: 'credit_card', label: 'Cartão de crédito', sub: 'Cobrança recorrente automática', icon: CreditCard },
];

const ERROR_MESSAGES: Record<string, string> = {
  asaas_not_configured: 'Gateway de pagamento ainda não foi configurado. Avise o suporte da Visual Software.',
  asaas_create_customer_failed: 'O gateway recusou os dados do seu cadastro. Verifique CNPJ/CPF e dados de contato.',
  asaas_create_subscription_failed: 'O gateway recusou a criação da assinatura. Tente novamente ou troque a forma de pagamento.',
  tenant_missing_email: 'Cadastre um email primário antes de ativar.',
  tenant_missing_name: 'Cadastre a razão social/nome da empresa antes de ativar.',
  tenant_missing_document: 'Cadastre seu CNPJ ou CPF antes de ativar a assinatura.',
  plan_not_found: 'Plano não encontrado. Recarregue a página e tente novamente.',
  no_subscription: 'Não encontramos sua assinatura. Recarregue a página.',
};

function extractActivateErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string; message?: string; detail?: string } }; message?: string };
  const data = e?.response?.data;
  if (data?.error && ERROR_MESSAGES[data.error]) {
    return data.message ? `${ERROR_MESSAGES[data.error]} (${data.message})` : ERROR_MESSAGES[data.error];
  }
  if (data?.message) return data.message;
  if (data?.detail) return data.detail;
  return e?.message ?? 'Erro desconhecido. Tente novamente em alguns segundos.';
}

// -----------------------------------------------------------------------
// Verifica se o tenant tem os dados mínimos para ativar a assinatura
// -----------------------------------------------------------------------
function tenantDataComplete(tenant: any): boolean {
  const doc = tenant?.cnpj || tenant?.cpf;
  return !!(doc && tenant?.phone && tenant?.address &&
    (() => { try { const a = JSON.parse(tenant.address); return a?.cep && a?.logradouro && a?.numero; } catch { return false; } })());
}

// -----------------------------------------------------------------------
// Formulário de dados cadastrais (pré-ativação)
// -----------------------------------------------------------------------
interface CadastroFormProps {
  tenant: any;
  onSaved: () => Promise<void>;
}

function CadastroForm({ tenant, onSaved }: CadastroFormProps) {
  const addr = (() => { try { return JSON.parse(tenant?.address || '{}'); } catch { return {}; } })();
  const hasCnpj = !!tenant?.cnpj;

  const [form, setForm] = useState({
    tipo: hasCnpj || !tenant?.cpf ? 'pj' : 'pf',
    cnpj: tenant?.cnpj || '',
    cpf: tenant?.cpf || '',
    phone: tenant?.phone || '',
    cep: addr?.cep || '',
    logradouro: addr?.logradouro || '',
    numero: addr?.numero || '',
    complemento: addr?.complemento || '',
    bairro: addr?.bairro || '',
    cidade: addr?.cidade || '',
    estado: addr?.estado || '',
  });
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  async function lookupCep(raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: data.logradouro ?? prev.logradouro,
          bairro:     data.bairro     ?? prev.bairro,
          cidade:     data.localidade ?? prev.cidade,
          estado:     data.uf         ?? prev.estado,
        }));
        toast.success('Endereço preenchido pelo CEP');
      } else {
        toast.error('CEP não encontrado.');
      }
    } catch {
      toast.error('Falha ao consultar CEP. Verifique sua conexão.');
    } finally {
      setCepLoading(false);
    }
  }

  function upd(k: keyof typeof form, v: string) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const doc = form.tipo === 'pj' ? form.cnpj.replace(/\D/g, '') : form.cpf.replace(/\D/g, '');
    if (!doc) { toast.error(form.tipo === 'pj' ? 'Informe o CNPJ.' : 'Informe o CPF.'); return; }
    if (!form.phone.trim()) { toast.error('Informe o telefone.'); return; }
    if (!form.cep.trim() || !form.numero.trim() || !form.logradouro.trim()) {
      toast.error('Informe CEP, logradouro e número do endereço.');
      return;
    }
    setSaving(true);
    try {
      const address = JSON.stringify({
        cep: form.cep.replace(/\D/g, ''),
        logradouro: form.logradouro.trim(),
        numero: form.numero.trim(),
        ...(form.complemento ? { complemento: form.complemento.trim() } : {}),
        ...(form.bairro ? { bairro: form.bairro.trim() } : {}),
        ...(form.cidade ? { cidade: form.cidade.trim() } : {}),
        ...(form.estado ? { estado: form.estado.trim().toUpperCase() } : {}),
      });
      await apiClient.updateTenant({
        ...(form.tipo === 'pj' ? { cnpj: doc } : { cpf: doc }),
        phone: form.phone.replace(/\D/g, ''),
        address,
      });
      toast.success('Dados atualizados!');
      await onSaved();
    } catch {
      toast.error('Erro ao salvar dados. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-800">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
        <span>Para ativar o plano precisamos dos seus dados cadastrais completos (exigidos pelo gateway de pagamento).</span>
      </div>

      {/* Tipo */}
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Tipo</div>
        <div className="grid grid-cols-2 gap-2">
          {(['pj', 'pf'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => upd('tipo', t)}
              className={cn(
                'rounded-lg border-2 px-3 py-2 text-sm transition-colors',
                form.tipo === t
                  ? 'border-brand-600 bg-brand-50/40 text-brand-700 font-medium'
                  : 'border-border hover:border-muted-foreground/40 text-muted-foreground',
              )}
            >
              {t === 'pj' ? 'Pessoa Jurídica (CNPJ)' : 'Pessoa Física (CPF)'}
            </button>
          ))}
        </div>
      </div>

      {/* Documento */}
      {form.tipo === 'pj' ? (
        <Input
          placeholder="CNPJ * (ex: 12.345.678/0001-90)"
          required
          value={form.cnpj}
          onChange={e => upd('cnpj', e.target.value)}
        />
      ) : (
        <Input
          placeholder="CPF * (ex: 123.456.789-00)"
          required
          value={form.cpf}
          onChange={e => upd('cpf', e.target.value)}
        />
      )}

      {/* Telefone */}
      <Input
        type="tel"
        placeholder="Telefone / WhatsApp *"
        required
        value={form.phone}
        onChange={e => upd('phone', e.target.value)}
      />

      {/* Endereço */}
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Endereço</div>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="CEP *"
              required
              value={form.cep}
              onChange={e => upd('cep', e.target.value)}
              onBlur={e => lookupCep(e.target.value)}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={cepLoading}
              onClick={() => lookupCep(form.cep)}
              title="Buscar endereço"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <Input placeholder="Número *" required value={form.numero} onChange={e => upd('numero', e.target.value)} />
          <Input
            placeholder="Logradouro (rua/avenida) *"
            required
            value={form.logradouro}
            onChange={e => upd('logradouro', e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Bairro" value={form.bairro} onChange={e => upd('bairro', e.target.value)} />
            <Input placeholder="Complemento" value={form.complemento} onChange={e => upd('complemento', e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Cidade" value={form.cidade} onChange={e => upd('cidade', e.target.value)} className="col-span-2" />
            <Input placeholder="UF" maxLength={2} value={form.estado} onChange={e => upd('estado', e.target.value.toUpperCase())} />
          </div>
        </div>
      </div>

      <Button type="submit" variant="gradient" className="w-full" loading={saving}>
        Salvar e continuar <ChevronRight className="h-4 w-4" />
      </Button>
    </form>
  );
}

// -----------------------------------------------------------------------
// Modal principal
// -----------------------------------------------------------------------
export function ActivateSubscriptionModal({ open, onOpenChange, plan, tvCount, billingCycle, allowEdit = false }: Props) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<PaymentMethod>('pix');
  const [coupon, setCoupon] = useState<string>('LANCAMENTO50');
  const [useCoupon, setUseCoupon] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [result, setResult] = useState<ActivateResponse | null>(null);
  const [cardReady, setCardReady] = useState<{ brand: string; last4: string } | null>(null);
  // Controla se o passo de dados cadastrais foi concluído
  const [dataStep, setDataStep] = useState<'checking' | 'needed' | 'done'>('checking');

  // Estado editável (só usado quando allowEdit=true — fluxo de reativação).
  // Default = valores atuais da subscription.
  const [selectedPlanId, setSelectedPlanId] = useState<number>(plan.id);
  const [selectedTvCount, setSelectedTvCount] = useState<number>(tvCount);
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>(billingCycle);

  // Carrega lista de planos pra renderizar o seletor (só quando edição ativa)
  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => apiClient.getPlans().then(r => r.data as Plan[]),
    enabled: open && allowEdit,
  });

  // Plano efetivo = selecionado (se edição) ou o prop. Fallback evita undefined
  // num render assíncrono antes do query de plans chegar.
  const effPlan: Plan = allowEdit ? (plans.find(p => p.id === selectedPlanId) ?? plan) : plan;
  const effTvCount = allowEdit ? selectedTvCount : tvCount;
  const effCycle = allowEdit ? selectedCycle : billingCycle;

  // Carrega dados do tenant pra verificar se estão completos
  const { data: tenant, refetch: refetchTenant } = useQuery({
    queryKey: ['tenant-for-activation'],
    queryFn: async () => {
      const res = await apiClient.getTenant();
      return res.data;
    },
    enabled: open,
  });

  // Quando tenant carrega, decide se precisa do passo de dados.
  // dataStep==='needed' pode ser forçado pelo onError do activateMutation.
  const tenantLoaded = tenant !== undefined;
  const effectiveDataStep = !tenantLoaded
    ? 'checking'
    : dataStep === 'needed' || (!tenantDataComplete(tenant) && dataStep !== 'done')
      ? 'needed'
      : 'done';

  const firstCents = effCycle === 'monthly' ? effPlan.first_tv_monthly_cents : Math.round(effPlan.first_tv_annual_cents / 12);
  const addCents = effCycle === 'monthly' ? effPlan.price_per_tv_monthly_cents : Math.round(effPlan.price_per_tv_annual_cents / 12);
  const monthlyEquiv = firstCents + (effTvCount - 1) * addCents;
  const couponDiscount = useCoupon && coupon === 'LANCAMENTO50' ? Math.round(monthlyEquiv * 0.5) : 0;
  const firstPayment = monthlyEquiv - couponDiscount;

  const activateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.activateSubscription({
        plan_id: effPlan.id,
        tv_count: effTvCount,
        billing_cycle: effCycle,
        payment_method: method,
        coupon_code: useCoupon && coupon.trim() ? coupon.trim().toUpperCase() : undefined,
      });
      return res.data as ActivateResponse;
    },
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ['subscription'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['tenant-access'] });
    },
  });

  // useEffect é mais confiável que onError para redirecionar ao passo de dados,
  // pois garante que a atualização de estado roda após o React confirmar o erro.
  useEffect(() => {
    if (!activateMutation.error) return;
    const code = (activateMutation.error as any)?.response?.data?.error as string | undefined;
    if (code === 'asaas_create_customer_failed' || code === 'tenant_missing_document' || code === 'tenant_missing_name') {
      refetchTenant();
      setDataStep('needed');
    }
  }, [activateMutation.error, refetchTenant]);

  function handleClose() {
    setResult(null);
    setCardReady(null);
    setDataStep('checking');
    activateMutation.reset();
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(true); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-background p-0 shadow-2xl">
          {/* Header */}
          <div className="gradient-brand text-white px-6 py-5 rounded-t-2xl relative overflow-hidden">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(600px 100px at 50% 0%, rgba(255,255,255,.35), transparent)' }} />
            <div className="flex items-start justify-between relative">
              <div>
                <Dialog.Title className="font-display text-2xl font-bold tracking-tight">
                  {result
                    ? 'Assinatura ativada! 🎉'
                    : effectiveDataStep === 'needed'
                      ? 'Dados cadastrais'
                      : allowEdit ? 'Reativar plano' : 'Ativar plano'}
                </Dialog.Title>
                <Dialog.Description className="text-sm text-white/90 mt-1">
                  {result
                    ? 'Sua assinatura foi criada com sucesso.'
                    : effectiveDataStep === 'needed'
                      ? 'Preencha seus dados antes de continuar'
                      : `Plano ${effPlan.name} · ${effTvCount} TV${effTvCount > 1 ? 's' : ''} · ${effCycle === 'monthly' ? 'Mensal' : 'Anual'}`}
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button className="rounded-md p-1 hover:bg-white/20 text-white" aria-label="Fechar">
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="p-6">
            {result ? (
              <ActivationSuccess result={result} method={method} onClose={handleClose} />
            ) : effectiveDataStep === 'checking' ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Carregando dados do cadastro…</div>
            ) : effectiveDataStep === 'needed' ? (
              <CadastroForm
                tenant={tenant}
                onSaved={async () => {
                  await refetchTenant();
                  setDataStep('done');
                }}
              />
            ) : (
              <>
                {/* Seletor inline de plano/TVs/ciclo — só na reativação */}
                {allowEdit && (
                  <div className="mb-5 space-y-4">
                    {/* Toggle ciclo */}
                    <div className="flex justify-center">
                      <div className="inline-flex rounded-lg border bg-muted p-1">
                        <button
                          type="button"
                          onClick={() => setSelectedCycle('monthly')}
                          className={cn(
                            'px-4 py-1.5 text-sm rounded-md transition-colors',
                            effCycle === 'monthly' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'
                          )}
                        >Mensal</button>
                        <button
                          type="button"
                          onClick={() => setSelectedCycle('annual')}
                          className={cn(
                            'px-4 py-1.5 text-sm rounded-md transition-colors',
                            effCycle === 'annual' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'
                          )}
                        >Anual <span className="text-emerald-600 font-semibold ml-1">-20%</span></button>
                      </div>
                    </div>

                    {/* Cards de planos */}
                    <div className="grid grid-cols-3 gap-2">
                      {plans.map(p => {
                        const isSel = p.id === selectedPlanId;
                        const first = effCycle === 'monthly' ? p.first_tv_monthly_cents : Math.round(p.first_tv_annual_cents / 12);
                        const add = effCycle === 'monthly' ? p.price_per_tv_monthly_cents : Math.round(p.price_per_tv_annual_cents / 12);
                        const total = first + (effTvCount - 1) * add;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelectedPlanId(p.id)}
                            className={cn(
                              'text-left rounded-lg border-2 p-3 transition-colors',
                              isSel ? 'border-brand-600 bg-brand-50/40' : 'border-border hover:border-muted-foreground/30',
                            )}
                          >
                            <div className="font-semibold text-sm">{p.name}</div>
                            <div className="text-lg font-bold mt-1">{formatCents(total)}<span className="text-xs text-muted-foreground font-normal">/mês</span></div>
                          </button>
                        );
                      })}
                    </div>

                    {/* TV count selector */}
                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Tv className="h-4 w-4 text-muted-foreground" />
                        <span>Quantidade de TVs</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={selectedTvCount <= 1}
                          onClick={() => setSelectedTvCount(c => Math.max(1, c - 1))}
                        ><Minus className="h-4 w-4" /></Button>
                        <span className="font-bold text-lg w-8 text-center">{effTvCount}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedTvCount(c => c + 1)}
                        ><Plus className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resumo do plano */}
                <div className="rounded-xl border border-border bg-muted/30 p-4 mb-5">
                  <div className="flex justify-between items-baseline">
                    <div className="text-sm text-muted-foreground">1ª TV (taxa base)</div>
                    <div className="font-medium">{formatCents(firstCents)}</div>
                  </div>
                  {effTvCount > 1 && (
                    <div className="flex justify-between items-baseline mt-1">
                      <div className="text-sm text-muted-foreground">{effTvCount - 1}× TV adicional</div>
                      <div className="font-medium">{formatCents((effTvCount - 1) * addCents)}</div>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between items-baseline mt-1 text-emerald-700">
                      <div className="text-sm">Cupom {coupon}</div>
                      <div className="font-medium">− {formatCents(couponDiscount)}</div>
                    </div>
                  )}
                  <div className="border-t border-border mt-3 pt-3 flex justify-between items-baseline">
                    <div className="font-semibold">Você paga agora</div>
                    <div className="text-2xl font-bold font-display">{formatCents(firstPayment)}</div>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="text-xs text-muted-foreground mt-2">
                      Cupom vale pros 3 primeiros meses. A partir do 4º mês, valor cheio de {formatCents(monthlyEquiv)}/mês.
                    </div>
                  )}
                </div>

                {/* Método de pagamento */}
                <div className="mb-5">
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                    Forma de pagamento
                  </div>
                  <div className="space-y-2">
                    {PAYMENT_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      const selected = method === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setMethod(opt.value); setCardReady(null); }}
                          className={cn(
                            'w-full flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors',
                            selected
                              ? 'border-brand-600 bg-brand-50/40'
                              : 'border-border hover:border-muted-foreground/30',
                          )}
                        >
                          <div className={cn(
                            'h-10 w-10 rounded-lg grid place-items-center shrink-0',
                            selected ? 'bg-brand-600 text-white' : 'bg-muted text-muted-foreground'
                          )}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-sm">{opt.label}</div>
                            <div className="text-xs text-muted-foreground">{opt.sub}</div>
                          </div>
                          <div className={cn(
                            'h-5 w-5 rounded-full border-2 grid place-items-center shrink-0',
                            selected ? 'border-brand-600 bg-brand-600' : 'border-muted-foreground/30',
                          )}>
                            {selected && <div className="h-2 w-2 rounded-full bg-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {method === 'credit_card' && !cardReady && (
                    <div className="mt-4 rounded-lg border-2 border-border bg-muted/20 p-4">
                      <CreditCardForm
                        submitLabel="Validar cartão"
                        onSuccess={(data) => setCardReady({ brand: data.card_brand, last4: data.card_last4 })}
                      />
                    </div>
                  )}
                  {method === 'credit_card' && cardReady && (
                    <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" />
                        <span><strong>{cardReady.brand}</strong> •••• {cardReady.last4} validado</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCardReady(null)}
                        className="text-xs underline text-emerald-700 hover:text-emerald-900"
                      >Trocar cartão</button>
                    </div>
                  )}
                </div>

                {/* Cupom */}
                <div className="mb-5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={useCoupon}
                      onChange={(e) => setUseCoupon(e.target.checked)}
                      className="rounded border-border"
                    />
                    <span>Tenho um cupom de desconto</span>
                  </label>
                  {useCoupon && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                        placeholder="Digite o cupom"
                        className="flex-1 bg-background border-2 border-border rounded-lg px-3 py-2 font-mono text-sm focus:border-brand-600 focus:outline-none"
                      />
                    </div>
                  )}
                  {useCoupon && coupon === 'LANCAMENTO50' && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                      <Sparkles className="h-3.5 w-3.5" />
                      Cupom de lançamento aplicado: 50% OFF nos 3 primeiros meses
                    </div>
                  )}
                </div>

                {/* Termos */}
                <label className="flex items-start gap-2 text-sm cursor-pointer mb-5">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="rounded border-border mt-0.5"
                  />
                  <span className="text-muted-foreground">
                    Concordo com os <a href="/termos" target="_blank" className="text-brand-700 underline">Termos de uso</a>
                    {' '}e a <a href="/privacidade" target="_blank" className="text-brand-700 underline">Política de privacidade</a>.
                  </span>
                </label>

                {activateMutation.isError && (() => {
                  const errCode = (activateMutation.error as any)?.response?.data?.error as string | undefined;
                  const showDataFix = errCode === 'asaas_create_customer_failed'
                    || errCode === 'tenant_missing_document'
                    || errCode === 'tenant_missing_name';
                  return (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <div className="font-semibold mb-1">Não foi possível ativar.</div>
                      <div className="text-xs">{extractActivateErrorMessage(activateMutation.error)}</div>
                      {showDataFix && (
                        <button
                          type="button"
                          onClick={() => { activateMutation.reset(); setDataStep('needed'); }}
                          className="mt-2 text-xs underline hover:no-underline"
                        >
                          Corrigir dados cadastrais (CNPJ/CPF e endereço)
                        </button>
                      )}
                    </div>
                  );
                })()}

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={handleClose}>Cancelar</Button>
                  <Button
                    variant="gradient"
                    className="btn-gradient-glow"
                    loading={activateMutation.isPending}
                    disabled={!acceptTerms || (method === 'credit_card' && !cardReady)}
                    onClick={() => activateMutation.mutate()}
                  >
                    {activateMutation.isPending
                      ? 'Ativando…'
                      : method === 'credit_card'
                        ? (cardReady ? 'Ativar plano agora' : 'Valide o cartão primeiro')
                        : <>Ativar e gerar {method === 'pix' ? 'PIX' : 'boleto'}</>}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// -----------------------------------------------------------------------
// Tela de sucesso
// -----------------------------------------------------------------------
function ActivationSuccess({ result, method, onClose }: { result: ActivateResponse; method: PaymentMethod; onClose: () => void }) {
  const payUrl = result.invoice_url || result.boleto_url;

  return (
    <div className="text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center mb-4">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      </div>
      <div className="font-display text-xl font-bold mb-2">Tudo certo, agora é só pagar.</div>
      <p className="text-sm text-muted-foreground mb-6">
        Total: <strong>{formatCents(result.total_cents)}</strong>. Sua assinatura fica ativa assim que o pagamento for confirmado.
      </p>

      {method === 'pix' && result.pix_copy_paste && (
        <div className="rounded-xl border-2 border-border p-4 mb-4 text-left">
          <div className="flex justify-center mb-4">
            <div className="bg-white p-3 rounded-lg border border-border">
              <QRCodeSVG value={result.pix_copy_paste} size={192} level="M" />
            </div>
          </div>
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">PIX copia-e-cola</div>
          <div className="font-mono text-xs bg-muted p-3 rounded break-all">{result.pix_copy_paste}</div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              navigator.clipboard.writeText(result.pix_copy_paste!);
              toast.success('Código PIX copiado');
            }}
          >
            Copiar código
          </Button>
        </div>
      )}

      {payUrl && (
        <Button asChild variant="gradient" className="btn-gradient-glow w-full mb-3">
          <a href={payUrl} target="_blank" rel="noopener noreferrer">
            Abrir página de pagamento <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      )}

      {!payUrl && (
        <div className="text-sm text-muted-foreground mb-4">
          Acompanhe o status no histórico de faturas abaixo.
        </div>
      )}

      <Button variant="outline" className="w-full" onClick={onClose}>
        Fechar
      </Button>
    </div>
  );
}
